import React from 'react';
import propTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { getMessage, getCropBasedMessage } from 'util/locale';
import { Modal, Button } from '@tcc/crate';
import { getActiveCropId } from 'current/currentSelectors';

// actions
import {
  closeSeedProductSelectorModal
} from 'current/uiwidgets';
import {
  resetSearchState,
  resetSeedSelectorProductInputs,
  searchSeedProductsFromSelector,
  setSelectedResultsToActive,
  updateSeedSelectorSearchParam
} from 'edit/seed-product-selector';
import { setSeedSelectorProductInput } from 'edit/seed-product-selector/product-inputs';

// selectors
import {
  getActiveCrop,
  getActiveCropDfpUuid,
  seedProductSelectorIsOpen
} from 'current/selectors';
import {
  getActiveSeedProductsByInput,
  getNumActiveSeedProducts,
  getSearchParams,
  getSearchResults,
  getSearchStatus,
  getSeedProductRmValuesForCrop,
  searchFormIsValid,
  hasDefinedRmRange
} from 'edit/seed-product-selector/selectors';
import {
  getBrandsByCrop
} from 'products/planting-selectors';
import {
  isAppMode,
  shouldShowWinfieldLink
} from 'settings/selectors';
import { getSeedProductSelectorValidationResult } from 'validations/selectors';

// util
import {
  generateWinfieldUrl
} from 'util/urls';

// components
import AddSeedProductsTrigger from 'edit/seed-product-selector/AddSeedProductsTrigger';
import SeedProductSelectPanel from 'edit/seed-product-selector/SeedProductSelectPanel';
import SeedProductSearchForm from 'edit/seed-product-selector/SeedProductSearchForm';

const baseString = 'vra-seed-product-selector-container';
let SeedProductSelectorContainer = ({
  activeSeedProductsByInput,
  brands,
  hasDefinedRmRange,
  isAppMode,
  isOpen,
  numActiveSeedProducts,
  onChangeBrand,
  onChangeRm,
  onClickAddSeedProductsTrigger,
  onClickCloseSeedProductSelector,
  onClickRemoveActiveProductTrigger,
  onClickSearchSeedProductsTrigger,
  onClickWinfieldLink,
  rmValues,
  searchFormIsValid,
  searchParams,
  searchResults,
  searchStatus,
  shouldShowWinfieldLink,
  validationResult,
  winfieldUrl
}) => {
  const cropId = useSelector(getActiveCropId);

  return isOpen ? (
    <Modal
      className={baseString}
      onClose={onClickCloseSeedProductSelector}
      show={true}
      size='XL'
    >
      <header className='vra-modal-header'>
        <div>
          <span>{getCropBasedMessage({ key: 'title-text__seed-finder', cropId })}</span>
        </div>
      </header>
      <section className={`${baseString}__body`}>
        <SeedProductSearchForm
          brands={brands}
          hasDefinedRmRange={hasDefinedRmRange}
          isAppMode={isAppMode}
          onChangeBrand={onChangeBrand}
          onChangeRm={onChangeRm}
          onClickSearchSeedProductsTrigger={onClickSearchSeedProductsTrigger}
          onClickWinfieldLink={() => { onClickWinfieldLink(winfieldUrl); }}
          rmValues={rmValues}
          searchFormIsValid={searchFormIsValid}
          searchParams={searchParams}
          shouldShowWinfieldLink={shouldShowWinfieldLink}
          validationResult={validationResult}
        />
        <SeedProductSelectPanel
          activeSeedProductsByInput={activeSeedProductsByInput}
          numActiveSeedProducts={numActiveSeedProducts}
          onClickRemoveActiveProductTrigger={onClickRemoveActiveProductTrigger}
          searchResults={searchResults}
          searchStatus={searchStatus}
        />
      </section>
      <footer>
        <AddSeedProductsTrigger
          numActiveSeedProducts={numActiveSeedProducts}
          onClickAddSeedProductsTrigger={onClickAddSeedProductsTrigger}
        />
        <Button
          className='vra-seed-product-selector-container__button--close'
          dataTestId={baseString}
          mode="secondary"
          onClick={onClickCloseSeedProductSelector}
        >
          {getMessage('button-text__close')}
        </Button>
      </footer>
    </Modal>
  ) : null;
};
SeedProductSelectorContainer.propTypes = {
  activeSeedProductsByInput: propTypes.object.isRequired,
  brands: propTypes.array.isRequired,
  hasDefinedRmRange: propTypes.bool.isRequired,
  isAppMode: propTypes.bool.isRequired,
  isOpen: propTypes.bool.isRequired,
  numActiveSeedProducts: propTypes.number.isRequired,
  onChangeBrand: propTypes.func.isRequired,
  onChangeRm: propTypes.func.isRequired,
  onClickAddSeedProductsTrigger: propTypes.func.isRequired,
  onClickCloseSeedProductSelector: propTypes.func.isRequired,
  onClickRemoveActiveProductTrigger: propTypes.func.isRequired,
  onClickSearchSeedProductsTrigger: propTypes.func.isRequired,
  onClickWinfieldLink: propTypes.func.isRequired,
  rmValues: propTypes.array.isRequired,
  searchFormIsValid: propTypes.bool.isRequired,
  searchParams: propTypes.object.isRequired,
  searchResults: propTypes.array.isRequired,
  searchStatus: propTypes.string.isRequired,
  shouldShowWinfieldLink: propTypes.bool.isRequired,
  validationResult: propTypes.object,
  winfieldUrl: propTypes.string.isRequired
};

SeedProductSelectorContainer = connect(
  (state) => {
    const crop = getActiveCrop(state);
    const cropUuid = getActiveCropDfpUuid(state);
    return {
      activeSeedProductsByInput: getActiveSeedProductsByInput(state),
      brands: getBrandsByCrop(state, cropUuid),
      rmValues: getSeedProductRmValuesForCrop(state, crop),
      isAppMode: isAppMode(state),
      isOpen: seedProductSelectorIsOpen(state),
      numActiveSeedProducts: getNumActiveSeedProducts(state),
      searchFormIsValid: searchFormIsValid(state),
      searchParams: getSearchParams(state),
      searchResults: getSearchResults(state),
      searchStatus: getSearchStatus(state),
      shouldShowWinfieldLink: shouldShowWinfieldLink(state),
      hasDefinedRmRange: hasDefinedRmRange(state),
      winfieldUrl: generateWinfieldUrl(state),
      validationResult: getSeedProductSelectorValidationResult(state)
    };
  },
  (dispatch) => {
    return {
      onChangeBrand: (brandName, brandUuid) => {
        dispatch(updateSeedSelectorSearchParam({ brandName, brandUuid }));
      },
      onChangeRm: (value, input) => dispatch(updateSeedSelectorSearchParam({ [`${input}`]: value })),
      onClickAddSeedProductsTrigger: () => {
        dispatch(setSelectedResultsToActive());
        dispatch(resetSearchState());
        dispatch(resetSeedSelectorProductInputs());
      },
      onClickCloseSeedProductSelector: () => {
        dispatch(closeSeedProductSelectorModal());
        dispatch(resetSearchState());
        dispatch(resetSeedSelectorProductInputs());
      },
      onClickRemoveActiveProductTrigger: (selectId) => dispatch(setSeedSelectorProductInput({ selectId })),
      onClickSearchSeedProductsTrigger: (e) => {
        e.preventDefault();
        dispatch(searchSeedProductsFromSelector());
      },
      onClickWinfieldLink: (winfieldUrl) => {
        window.location = winfieldUrl;
      }
    };
  }
)(SeedProductSelectorContainer);
export default SeedProductSelectorContainer;
